import { useState, type CSSProperties } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DEPLOYMENT_KIND_LABELS, DeploymentKind } from '@bhops/shared';
import { DeploymentStatusCell } from '@/lib/deployment-status-label';
import { deploymentsApi, type ListDeploymentsQuery } from '@/api/deployments';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';

function AcceptancePdfButton({ deploymentId }: { deploymentId: string }) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  async function handleClick() {
    setLoading(true);
    try {
      await deploymentsApi.downloadAcceptancePdf(deploymentId);
    } catch (err) {
      // R2 후속(워크플로 M17) — 다운로드 실패(404/권한/S3)가 조용히 삼켜져 무반응이던
      // 문제. 에러를 토스트로 표면화한다.
      toast.error(extractApiError(err), '인수확인서 다운로드 실패');
    } finally {
      setLoading(false);
    }
  }

  return (
    <button onClick={handleClick} disabled={loading} style={pdfBtn(loading)}>
      {loading ? '…' : '인수확인서'}
    </button>
  );
}

// 이름표 정본은 `@bhops/shared` 다 — 배치 창·이 목록·감사 장부가 같은 것을 쓴다.
// 종전엔 여기 따로 적혀 있어서, 종류를 늘릴 때마다 화면마다 다른 말이 됐다
// (2026-09-18 에 인증·테스트를 더하며 실제로 타입 검사가 잡았다).
const KIND_LABEL: Record<DeploymentKind, string> = DEPLOYMENT_KIND_LABELS;

const PAGE_SIZE = 50;

interface DeploymentRow {
  id: string;
  kind: DeploymentKind;
  deployedAt: string;
  returnedAt: string | null;
  robot?: {
    id: string;
    serialShort: string | null;
    serialLong: string | null;
    model?: { displayName?: string };
  };
  store?: { id: string; name: string; branchName: string | null };
  customer?: { id: string; displayName: string } | null;
}

interface Props {
  /**
   * Plan v0.3.0 §F-3 — embed mode for CustomerMasterPage's sub-tab.
   * Hides the page h1 (parent shows the customer's header).
   */
  embedded?: boolean;
  /**
   * §F-3 — when set, filter the list to this customer's deployments
   * only. Passed through to `deploymentsApi.list({ customerId })`.
   */
  customerId?: string;
}

export function DeploymentsPage({ embedded = false, customerId }: Props = {}) {
  const [filters, setFilters] = useState<ListDeploymentsQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    activeOnly: false,
    ...(customerId ? { customerId } : {}),
  });

  // Always force the customerId filter when embedded so the user
  // can't override it via other filter controls.
  const effectiveFilters: ListDeploymentsQuery = customerId ? { ...filters, customerId } : filters;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['deployments', effectiveFilters],
    queryFn: () => deploymentsApi.list(effectiveFilters),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  // Backend returns rows with joined robot/store/customer (see DeploymentsService.list
  // `include`); the shared `DeploymentSummaryDto` only models bare IDs, so we narrow here.
  const items = (data?.items ?? []) as unknown as DeploymentRow[];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: embedded ? 8 : 16,
        height: '100%',
      }}
    >
      {/* §F-3 — page header hidden in embedded mode (parent shows customer). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!embedded && (
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            배치 이력
          </h1>
        )}
        <select
          value={filters.kind ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              kind: (e.target.value || undefined) as DeploymentKind | undefined,
              page: 1,
            }))
          }
          style={select}
        >
          <option value="">전체 유형</option>
          {Object.entries(KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={Boolean(filters.activeOnly)}
            onChange={(e) => setFilters((f) => ({ ...f, activeOnly: e.target.checked, page: 1 }))}
          />
          활성만
        </label>
        <div
          className="kuun-caption"
          style={{ marginLeft: 'auto', color: isFetching ? 'var(--brand)' : 'var(--fg-muted)' }}
        >
          {isFetching ? '불러오는 중…' : `총 ${total.toLocaleString()} 건`}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <div style={empty}>
            <span className="kuun-caption">불러오는 중…</span>
          </div>
        ) : error ? (
          <div style={empty}>
            <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</span>
          </div>
        ) : items.length === 0 ? (
          <div style={empty} className="kuun-dot-grid">
            <div className="kuun-h4">조건에 맞는 배치가 없습니다</div>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>유형</th>
                <th style={th}>로봇</th>
                <th style={th}>모델</th>
                <th style={th}>매장</th>
                <th style={th}>고객사</th>
                <th style={th}>배치일</th>
                <th style={th}>회수일</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const sn = d.robot?.serialShort
                  ? `#${d.robot.serialShort}`
                  : (d.robot?.serialLong ?? '–');
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={td}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 'var(--r-pill)',
                          fontSize: 11,
                          background: 'var(--bg-sunken)',
                          color: 'var(--fg-muted)',
                        }}
                      >
                        {KIND_LABEL[d.kind]}
                      </span>
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {d.robot ? (
                        <Link to={`/robots/${d.robot.id}`} style={{ color: 'var(--fg-brand)' }}>
                          {sn}
                        </Link>
                      ) : (
                        sn
                      )}
                    </td>
                    <td style={td}>{d.robot?.model?.displayName ?? '–'}</td>
                    <td style={td}>
                      {d.store ? [d.store.name, d.store.branchName].filter(Boolean).join(' ') : '–'}
                    </td>
                    <td style={td}>{d.customer?.displayName ?? '–'}</td>
                    {/* 추가수정 2차(26-07-27) 정정 — 같은 Deployment 를 로봇 상세·
                        매장 상세는 이미 kstYmd("2026.07.27")로 그리는데 여기만
                        toLocaleDateString("2026. 7. 27.") 이라 한 데이터가 두 표기로
                        갈렸다. 결정 ②A(목록은 날짜만)대로 KST 날짜 표기로 통일. */}
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      {kstYmd(d.deployedAt) ?? '–'}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      <DeploymentStatusCell
                        kind={d.kind}
                        returnedAt={d.returnedAt}
                        returnedLabel={kstYmd(d.returnedAt) ?? '–'}
                      />
                    </td>
                    <td style={td}>
                      <AcceptancePdfButton deploymentId={d.id} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, (f.page ?? 1) - 1) }))}
          disabled={page <= 1}
          style={pagerBtn(page <= 1)}
        >
          이전
        </button>
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          {page} / {totalPages}
        </div>
        <button
          onClick={() =>
            setFilters((f) => ({ ...f, page: Math.min(totalPages, (f.page ?? 1) + 1) }))
          }
          disabled={page >= totalPages}
          style={pagerBtn(page >= totalPages)}
        >
          다음
        </button>
      </div>
    </div>
  );
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};
const select: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
function pagerBtn(disabled: boolean): CSSProperties {
  return {
    height: 28,
    padding: '0 12px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--r-btn)',
    background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
    color: disabled ? 'var(--fg-subtle)' : 'var(--fg-default)',
    fontSize: 12,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function pdfBtn(disabled: boolean): CSSProperties {
  return {
    height: 24,
    padding: '0 8px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--r-btn)',
    background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
    color: disabled ? 'var(--fg-subtle)' : 'var(--fg-brand)',
    fontSize: 11,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  };
}
